/**
 * ============================================================================
 * ⭐⭐ WHAT ORDER THE DOCK DOOR ROWS APPEAR IN
 * ============================================================================
 *
 * > Pete, 2026-09-10: *"re-arrange the Dock Door Rows (not unassigned) with a
 * > icon up next to Door that rearrancges the doors by if they have meetings or
 * > not, then by dock door order. So all the doors that have meetings come to the
 * > top of the calendar and the doors that have no appointments on the schedule
 * > fall to the bottom … Finally, the scheduler should have the ability to drag a
 * > row above others to place it where she wants."*
 *
 * > And on how the control behaves: *"It doesn't run automatically, its a view
 * > button at the top, next to DOOR (above Unassigned). Click on it and it sorts
 * > how we said. Click on it again and it resets back to the original layout."*
 *
 * ---- ⛔⛔ IT NEVER RUNS BY ITSELF, AND THAT IS A SAFETY RULE ---------------
 *
 * ⚠️ A door that takes its first booking at 10am would, under a live sort, JUMP
 * to the top mid-morning — possibly while somebody is dragging an appointment
 * onto it, because these rows carry create, move and resize drags. ⛔ A row that
 * moves under the cursor is not a cosmetic problem.
 *
 * ⭐ SO THE SORT IS A SNAPSHOT TAKEN ON A CLICK. Nothing here subscribes to
 * anything; `sortBusyFirst` is a pure function of the arguments it is handed at
 * the moment the button is pressed.
 *
 * ---- ⭐ THE UNASSIGNED ROW IS NOT A DOOR AND IS NEVER MOVED ----------------
 *
 * ⛔ Pete: *"(not unassigned)"*. It is a synthetic row (`UNASSIGNED_DOOR_ID`)
 * that is also the drop target for a truck with no door, and it is always first.
 * ⚠️ Nothing in this module ever sees it: the caller sorts `doors` and prepends
 * the queue afterwards, so there is no path by which a bug here could bury it.
 *
 * ---- ⚠️ THREE ORDERS, AND WHICH ONE WINS -----------------------------------
 *
 *   SERVER  — `displayOrder` from the database. The site's physical layout.
 *   MANUAL  — what the scheduler dragged. Overrides SERVER when it exists.
 *   BUSY    — the toggle. Partitions whichever of the two is in force.
 *
 * ⭐ SO THE TOGGLE'S "OFF" IS NOT "SERVER ORDER" — it is *the order the board had
 * before the sort*, which is her manual order if she made one. ⚠️ Pete said *"it
 * resets back to the original layout"*, and for somebody who has never dragged a
 * row those are the same sentence. For somebody who has, throwing her arrangement
 * away because she used a view toggle would be the wrong reading of it.
 * ============================================================================
 */

/** ⛔ Per FACILITY. Brighton has 23 doors and Reno has 6; one shared list would be nonsense. */
const KEY = (facilityCode: string) => `dock.doorOrder.v1.${facilityCode}`;

export interface OrderableDoor {
    dockDoorId: number;
    displayOrder: number;
}

/**
 * ⚠️ THROWS IN PRIVATE MODE rather than returning null — the same note as
 * `readOpenCards` in `CheckInPanel`. Every access is wrapped.
 */
export function readDoorOrder(facilityCode: string): number[] | null {
    try {
        const raw = window.localStorage.getItem(KEY(facilityCode));
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        const ids = parsed.filter((x): x is number => typeof x === 'number' && Number.isFinite(x));
        return ids.length > 0 ? ids : null;
    } catch {
        return null;
    }
}

export function writeDoorOrder(facilityCode: string, ids: number[]): void {
    try {
        window.localStorage.setItem(KEY(facilityCode), JSON.stringify(ids));
    } catch {
        /* ⚠️ Quota or blocked site data. The order still holds for this session;
           it simply will not be remembered. Nothing to interrupt anybody with. */
    }
}

export function clearDoorOrder(facilityCode: string): void {
    try { window.localStorage.removeItem(KEY(facilityCode)); } catch { /* see above */ }
}

/**
 * ---- ⛔⛔ A SAVED ORDER IS A HINT, NEVER THE POPULATION --------------------
 *
 * ⚠️ THE LIST IN localStorage IS A SNAPSHOT OF THE DOORS THAT EXISTED WHEN SHE
 * DRAGGED. Doors get added, renamed and deactivated (`DoorsAdmin`, migration
 * 173), and a stored list is not told about any of it.
 *
 * ⛔ SO IT IS APPLIED AS A SORT KEY, NOT AS A FILTER. A door missing from the
 * saved list still appears — after the known ones, in server order. A door in the
 * saved list that no longer exists is ignored. ⚠️ The failure this avoids is the
 * one that matters: a NEW DOCK DOOR THAT NEVER SHOWS UP ON THE BOARD, which
 * presents as "the door isn't there" and would be chased in the admin screen.
 */
export function applyManualOrder<T extends OrderableDoor>(doors: T[], manual: number[] | null): T[] {
    if (!manual || manual.length === 0) return doors;
    const rank = new Map<number, number>();
    manual.forEach((id, i) => rank.set(id, i));
    /* ⭐ Unknown doors sort AFTER every known one, and keep server order among
       themselves — `manual.length + index` does both in one number. */
    return [...doors]
        .map((d, i) => ({ d, k: rank.has(d.dockDoorId) ? rank.get(d.dockDoorId)! : manual.length + i }))
        .sort((a, b) => a.k - b.k)
        .map(x => x.d);
}

/**
 * ---- ⭐ DOORS WITH WORK ON TOP, EVERYTHING ELSE UNDERNEATH -----------------
 *
 * ⛔ A STABLE PARTITION, NOT A COMPARATOR ON A BOOLEAN. Within each half the
 * incoming order is preserved exactly — which is what makes Pete's *"then by dock
 * door order"* true whether the incoming order is the server's or hers.
 *
 * ⚠️ `Array.prototype.sort` IS SPEC-STABLE since ES2019, but a comparator that
 * returns 0 for two busy doors leaves their relative order to that guarantee and
 * to whatever `hasAppointments` says on the day. Two `filter` passes state the
 * intent instead of relying on it.
 */
export function sortBusyFirst<T extends OrderableDoor>(
    doors: T[], hasAppointments: (door: T) => boolean,
): T[] {
    const busy = doors.filter(d => hasAppointments(d));
    const idle = doors.filter(d => !hasAppointments(d));
    return [...busy, ...idle];
}

/**
 * ---- ⭐⭐ IS THE BOARD ACTUALLY IN THE SITE'S ORDER? (Pete, 2026-09-19) --------
 *
 * > Pete: *"Can the site order only show when its not in site order and can the
 * > column expand and contract based on that scenario?"*
 *
 * ⛔⛔ "A MANUAL ORDER EXISTS" AND "THE BOARD IS NOT IN SITE ORDER" ARE NOT THE
 * SAME CLAIM, and the ↺ control was testing the first while its label promised the
 * second. Drag a row down and back, or drag the row that was already in that
 * position, and `manualOrder` is a non-null array that describes exactly the site's
 * order — leaving a button on screen offering to restore what is already there.
 *
 *   ** A CONTROL WHOSE ENABLED-NESS TESTS WHETHER A PREFERENCE WAS EVER SET, RATHER
 *      THAN WHETHER IT CURRENTLY CHANGES ANYTHING, IS OFFERING A NO-OP. **
 *
 * ⚠️ THE COMPARISON IS AGAINST `applyManualOrder`'s OWN OUTPUT, not against a
 * re-sort written here. That matters because of the unknown-door rule above: a door
 * missing from the saved list is appended rather than dropped, so a hand-rolled
 * "sort by displayOrder and compare" would disagree with what is actually rendered
 * the first time a new dock door is added. ✅ Comparing the function's result to its
 * input asks the only question that matters — *does applying this change anything?*
 *
 * ⛔ `busyFirst` IS DELIBERATELY NOT CONSIDERED. It is a view toggle with its own
 * control and its own off state; the ↺ button exists to discard a DRAGGED order.
 */
export function isSiteOrder<T extends OrderableDoor>(doors: T[], manual: number[] | null): boolean {
    if (!manual || manual.length === 0) return true;
    const applied = applyManualOrder(doors, manual);
    return applied.every((d, i) => d.dockDoorId === doors[i].dockDoorId);
}

/**
 * ---- ⭐⭐ #76 / DECISION 213 — SORT THE DOOR NAMES (Pete, 2026-09-22) ---------
 *
 * > Pete: *"We need to add Ascending/Descending to the Door Column (maybe arrows)?"*
 *
 * ⛔⛔ NATURAL ORDER, NOT LEXICAL, AND THAT IS THE WHOLE POINT OF THE KEY.
 * `['Dock Door 9', 'Dock Door 10', 'Dock Door 89'].sort()` puts **9 last**, because
 * a string sort compares `'9'` against `'1'` and `'8'` one character at a time. On a
 * board of twenty-three doors that reads as a broken control, not as a sort.
 *
 *   ** A SORT THAT ORDERS THE LABEL LEXICALLY IS ORDERING THE DIGITS, NOT THE
 *      DOORS. **
 *
 * ⭐ `localeCompare` with `numeric: true` is the browser's own implementation of
 * this, so there is no hand-rolled digit scanner to get wrong. `sensitivity: 'base'`
 * so `dock door 9` and `Dock Door 9` do not sort into two groups.
 */
export type DoorSortDir = 'asc' | 'desc';

export function compareDoorNames(a: string, b: string): number {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * ⭐ THE ONE FUNCTION THE COMPONENT CALLS. Manual, then the NAME SORT, then the
 * partition — so the partition always operates on the order she is actually
 * looking at, and the name sort orders WITHIN each half of it.
 *
 * ---- ⛔⛔ WHY THE NAME SORT GOES **BEFORE** sortBusyFirst -------------------
 *
 * Pete chose *"a second, separate control"* over one that cycles: the busy-first
 * toggle GROUPS, the arrows ORDER. Running the name sort first and partitioning
 * afterwards gives exactly that — busy doors in name order, then idle doors in
 * name order. ⚠️ `sortBusyFirst` is a stable two-bucket filter, so it preserves
 * whatever order it was handed; reversing these two steps would instead throw the
 * grouping away.
 *
 * ---- ⛔⛔⛔ AND IT IS A NO-OP WHEN OFF, WHICH IS NOT AN ACCIDENT --------------
 *
 * `nameSort` omitted or null ⇒ this function is byte-identical to what it was
 * before 2026-09-22. A new control that quietly changed the board for everyone who
 * never touched it would be the worst outcome available here, because the board's
 * default order is one Pete has already dragged into shape.
 *
 * ⚠️ THE DIRECTION AND THE ACCESSOR TRAVEL TOGETHER, IN ONE OBJECT, ON PURPOSE.
 * Two separate optional arguments would let a caller pass a direction with no way
 * to read a name — and the sort would then silently do nothing.
 *   ** AN OPTIONAL ARGUMENT THAT DISABLES A RULE IS A RULE THAT IS OFF BY
 *      DEFAULT, AND NOBODY READS A DEFAULT. **
 *
 * ⚠️ `nameOf` RATHER THAN A `name` FIELD ON `OrderableDoor`. Two boards call this
 * and only one of them sorts; widening the interface would make every caller carry
 * a field it does not use.
 */
export function orderDoors<T extends OrderableDoor>(
    doors: T[],
    manual: number[] | null,
    busyFirst: boolean,
    hasAppointments: (door: T) => boolean,
    nameSort?: { dir: DoorSortDir; nameOf: (door: T) => string } | null,
): T[] {
    const base = applyManualOrder(doors, manual);
    /* ⚠️ A COPY. `applyManualOrder` returns a fresh array today, but `sort` mutates
       in place and a future change to that function would make this quietly
       reorder its caller's data.
         ** AN IN-PLACE SORT IS A SIDE EFFECT WEARING THE COSTUME OF A RESULT. ** */
    const named = nameSort
        ? [...base].sort((x, y) => {
            const c = compareDoorNames(nameSort.nameOf(x), nameSort.nameOf(y));
            return nameSort.dir === 'desc' ? -c : c;
        })
        : base;
    return busyFirst ? sortBusyFirst(named, hasAppointments) : named;
}

/**
 * ---- ⛔⛔⚤ THE DRAG EDITS HER ARRANGEMENT, NOT THE SORT'S OUTPUT (2026-09-19)
 *
 * > Pete: *"Doors with Appointments on top is not working now"* → *"After you move
 * > a row"*.
 *
 * ⛔⛔ REPRODUCED EXACTLY, WITH THESE FUNCTIONS, NO GUESSING:
 *
 *     1. site order                 119 120 121 122 123 124 125
 *     2. click the toggle           123 125 119 120 121 122 124   <- works
 *     3. drag 119 above 120         123 125 119 120 121 122 124
 *        SAVED                      123 125 119 120 121 122 124   <- the SORT got saved
 *     4. click the toggle           123 125 119 120 121 122 124   <- identical
 *     5. click it off               123 125 119 120 121 122 124   <- identical
 *
 * The drop handlers saved *the order on screen*, and while the partition is on,
 * the order on screen IS the partition's output. One drag therefore froze the
 * busy sort into the persisted arrangement, after which the toggle could never
 * change anything again, in either direction. It was not dead — it was sorting an
 * order that was already sorted.
 *
 *   ** A VIEW TOGGLE'S OUTPUT WAS PERSISTED AS A PREFERENCE, AND A PREFERENCE
 *      CANNOT BE TOGGLED OFF. **
 *
 * ⚠️ The old reasoning was not wrong, it was incomplete. It weighed "save the
 * order on screen" against "save the SERVER order" — and saving the server order
 * really would rearrange the board the moment she turned the sort off. ✅ There is
 * a third order, and it is the right one: **the arrangement underneath the
 * partition**, i.e. `applyManualOrder(doors, manual)`. The partition is a view;
 * her arrangement is the document; a drag edits the document.
 *
 *   ** WHEN TWO OPTIONS ARE BOTH WRONG, THE ANSWER IS USUALLY A THIRD ONE THAT
 *      WAS NEVER LISTED, NOT THE LESS WRONG OF THE TWO. **
 *
 * ⚠️ WITH THE SORT OFF — the ordinary case — the base order and the visible order
 * are the same list, so this is a no-op change. It only bites in the case that was
 * broken.
 *
 * ⛔ THE CALLERS NO LONGER COMPOSE THIS THEMSELVES. Both boards used to build a
 * `visible` id list out of their own `rows` and hand it to `moveDoorBefore`; that
 * is precisely where the bug lived, in two copies. Returns `null` when nothing
 * moved, so a caller can skip the write without an identity comparison.
 */
export function moveDoorInBaseOrder<T extends OrderableDoor>(
    doors: T[], manual: number[] | null, dragged: number, target: number,
): number[] | null {
    const base = applyManualOrder(doors, manual).map(d => d.dockDoorId);
    const next = moveDoorBefore(base, dragged, target);
    return next === base ? null : next;
}

/**
 * ---- ⛔⛔⚤ A DRAG WHILE THE PARTITION IS ON REORDERS **WITHIN THE GROUP** -----
 *
 * > Pete: *"I put appointments up top but now when I drag a row, it drags more
 * > than one row with it"*
 *
 * ⛔⛔ MEASURED: **6 of 7 rows moved.** The drop applied her move AND switched the
 * partition off in the same frame, so every busy door fell back to its home
 * position at the moment she let go.
 *
 *     sort ON (what she sees)  DD123 DD125 DD119 DD120 DD121 DD122 DD124
 *     drag DD125 above DD119
 *     after the drop           DD125 DD119 DD120 DD121 DD122 DD123 DD124
 *                              ^ six rows changed position. She moved one.
 *
 * ⚠️ THE PREVIOUS FIX WAS NOT WRONG, IT WAS INCOMPLETE — it stopped the partition
 * being frozen into the saved order, and that exposed the ambiguity the freezing
 * had been hiding: **a position in the sorted view is not a position in her
 * arrangement**, and the old code "resolved" it by making the view permanent.
 *
 *   ** A FIX THAT REMOVES A WORKAROUND INHERITS EVERY PROBLEM THE WORKAROUND WAS
 *      COVERING, AND THEY ARRIVE LOOKING LIKE NEW BUGS. **
 *
 * ---- ✅ WHAT MAKES THIS SOLVABLE: THE PARTITION IS ORDER-PRESERVING ----------
 *
 * `sortBusyFirst` is two `filter` passes, so **the relative order inside each half
 * is exactly the base order's**. That means a move *within* a half can be written
 * back into the base order with no ambiguity at all: read the new within-busy and
 * within-idle sequences off the dragged view, then refill the base order's own
 * busy/idle SLOTS with them. Re-applying the partition then reproduces precisely
 * what she dropped. ✅ Measured at **2 rows moved** — the one she dragged and the
 * one it displaced.
 *
 * ⛔ ACROSS the halves there is no honest answer: the partition would re-hoist the
 * row on the very next render, so the drop is REFUSED rather than silently undone.
 * `canDropDoorRow` lets the caller say so with the cursor instead of accepting a
 * gesture it intends to discard.
 *
 *   ** A DROP TARGET THAT ACCEPTS A GESTURE IT WILL NOT HONOUR IS WORSE THAN ONE
 *      THAT REFUSES IT, BECAUSE THE USER LEARNS THE WRONG RULE. **
 *
 * ⛔⛔ AND THE SORT NO LONGER SWITCHES ITSELF OFF ON A DROP. That call existed to
 * stop the partition immediately moving the row she had just hand-placed — which
 * it no longer can, because the placement is expressed in the partition's own
 * terms. Turning it off was the unwind she is reporting.
 *
 * ⚠️ WITH THE SORT OFF this delegates to `moveDoorInBaseOrder` and nothing about
 * the ordinary case changes.
 */
export function moveDoorForDrop<T extends OrderableDoor>(
    doors: T[], manual: number[] | null, busyFirst: boolean,
    hasAppointments: (door: T) => boolean,
    dragged: number, target: number,
): number[] | null {
    if (!busyFirst) return moveDoorInBaseOrder(doors, manual, dragged, target);

    const base = applyManualOrder(doors, manual);
    const baseIds = base.map(d => d.dockDoorId);
    const busyOf = new Map<number, boolean>(base.map(d => [d.dockDoorId, hasAppointments(d)]));
    /* ⛔ Unknown id, or a cross-group drop: nothing this function could return
       would survive the next render. */
    if (!busyOf.has(dragged) || !busyOf.has(target)) return null;
    if (busyOf.get(dragged) !== busyOf.get(target)) return null;

    const visible = sortBusyFirst(base, hasAppointments).map(d => d.dockDoorId);
    const nextVisible = moveDoorBefore(visible, dragged, target);
    if (nextVisible === visible) return null;

    /* ⭐ Refill the BASE order's own slots. The slot pattern (which positions hold a
       busy door) is left exactly as it was; only the contents of each half are
       resequenced. That is what keeps the arrangement coherent when the partition
       is later turned off. */
    const nextBusy = nextVisible.filter(id => busyOf.get(id));
    const nextIdle = nextVisible.filter(id => !busyOf.get(id));
    let bi = 0, ii = 0;
    return baseIds.map(id => (busyOf.get(id) ? nextBusy[bi++] : nextIdle[ii++]));
}

/**
 * ⭐ MAY THIS ROW BE DROPPED ON THAT ONE? Asked during `dragover`, where the
 * payload cannot be read (the browser withholds `getData` until `drop`), so the
 * caller passes the id it stashed in state when the drag began.
 * ⛔ `null` dragged id means "not a row drag" — refuse rather than guess.
 */
export function canDropDoorRow<T extends OrderableDoor>(
    doors: T[], busyFirst: boolean, hasAppointments: (door: T) => boolean,
    dragged: number | null, target: number,
): boolean {
    if (dragged === null || dragged === target) return false;
    if (!busyFirst) return true;
    const byId = new Map(doors.map(d => [d.dockDoorId, d]));
    const a = byId.get(dragged);
    const b = byId.get(target);
    if (!a || !b) return false;
    return hasAppointments(a) === hasAppointments(b);
}

/**
 * ⭐ MOVE ONE ROW **ABOVE** ANOTHER, returning the new id list.
 *
 * ---- ⛔ "ABOVE THE ROW YOU DROP ON" IS THE WHOLE RULE, BOTH DIRECTIONS ------
 *
 * Pete: *"drag a row above others to place it where she wants."* ⚠️ Dragging
 * DOWNWARD can feel like it stopped short — drop door 2 on door 4 and it lands at
 * position 3, not 4 — but the alternative is "above when dragging up, below when
 * dragging down", which is two rules and cannot be predicted from the cursor.
 * ⭐ ONE RULE. It was worth being sure about: the first test written for this
 * asserted the OTHER behaviour and the code was right.
 *
 * ⛔ IT WORKS ON IDS, NOT INDICES. The caller has two `dockDoorId`s from a drag;
 * translating those to indices in the component is where an off-by-one would live
 * and would be invisible until somebody dragged the last row.
 *
 * ⚠️ DROPPING A ROW ON ITSELF IS A NO-OP, and returns the SAME ARRAY so a caller
 * comparing by identity can skip the write.
 */
export function moveDoorBefore(ids: number[], dragged: number, target: number): number[] {
    if (dragged === target) return ids;
    const from = ids.indexOf(dragged);
    const to = ids.indexOf(target);
    if (from < 0 || to < 0) return ids;
    const next = [...ids];
    next.splice(from, 1);
    /* ⛔⛔ RE-FIND THE TARGET AFTER THE REMOVAL. Splicing out an element ABOVE the
       target shifts the target up by one, so the pre-removal index lands the row
       one place too low. ⚠️ IT AGREES WITH THE CORRECT VERSION WHEN DRAGGING UP,
       which is exactly why it survives a casual test — the suite runs both
       versions and asserts they DIFFER downward and AGREE upward. */
    next.splice(next.indexOf(target), 0, dragged);
    return next;
}
